import CheckboxInput from 'lib/components/Checkbox/CheckboxInput';
import { Form } from 'lib/components/Form';
import React, { useState } from 'react';
import { DEFAULT_OPTIMIZER_HYPERPARAMS } from 'lib/services/publisherPaginationSettingsService';
import {
  DEFAULT_LAYOUT_SETTINGS,
  DEFAULT_SEPARATOR_CONFIG,
  PublisherPaginationSettings
} from 'lib/types/publisherPaginationSettings';
import { ESnapshotExists } from 'lib/types';
import { GridInput } from 'lib/components/Card/Grid';
import { SwitchControlledCard } from 'lib/components/Card/SwitchControlledCard';
import { TextField } from 'lib/components/TextField';
import { Autocomplete } from 'lib/components/Autocomplete';
import { ColumnButton } from 'lib/components/ColumnButton';
import { columnObjectsAreEqual } from 'lib/utils/stringify';
import { EditableCardArray } from 'lib/components/Card/EditableCardArray';
import { cdnIfy, guidGenerator, unCdnify } from 'lib/helpers';

const extractRelevantPublisherPaginationSettings = (
  autoPaginationSettings: ESnapshotExists<PublisherPaginationSettings>
): Pick<
  PublisherPaginationSettings,
  | 'variants'
  | 'separators'
  | 'bordering'
  | 'optimizerHyperparams'
  | 'fillers'
  | 'layoutSettings'
  | 'headerSettings'
  | 'externalAdSettings'
> => {
  return {
    variants: autoPaginationSettings.data().variants || {
      disabled: false
    },
    separators:
      autoPaginationSettings.data().separators || DEFAULT_SEPARATOR_CONFIG,
    bordering: autoPaginationSettings.data().bordering || undefined,
    optimizerHyperparams:
      autoPaginationSettings.data().optimizerHyperparams || undefined,
    fillers: autoPaginationSettings.data().fillers || [],
    layoutSettings:
      autoPaginationSettings.data().layoutSettings || DEFAULT_LAYOUT_SETTINGS,
    headerSettings: autoPaginationSettings.data().headerSettings || undefined,
    externalAdSettings:
      autoPaginationSettings.data().externalAdSettings || undefined
  };
};

export default function OrderPaginationSettingsTab({
  autoPaginationSettings
}: {
  autoPaginationSettings: ESnapshotExists<PublisherPaginationSettings>;
}) {
  const [
    relevantPublisherPaginationSettings,
    setRelevantPublisherPaginationSettings
  ] = useState(
    extractRelevantPublisherPaginationSettings(autoPaginationSettings)
  );
  const [saving, setSaving] = useState(false);
  const updateSettings = async () => {
    setSaving(true);
    try {
      await autoPaginationSettings.ref.update({
        ...relevantPublisherPaginationSettings
      });
    } catch (error) {
      window.alert(`Error updating settings: ${error}`);
    } finally {
      setSaving(false);
      window.location.reload();
    }
  };

  const unchanged = columnObjectsAreEqual(
    relevantPublisherPaginationSettings,
    extractRelevantPublisherPaginationSettings(autoPaginationSettings)
  );

  return (
    <Form onSubmit={updateSettings}>
      <div className="flex flex-col gap-4">
        <div className="text-lg font-bold">Layout Settings</div>
        <div className="grid grid-cols-2 gap-4">
          <GridInput fullWidth>
            <CheckboxInput
              labelText="Disable splitting across columns"
              labelDescription="Notices will only be split across columns if they exceed the page height."
              id="disableSplittingAcrossColumns"
              checked={!!relevantPublisherPaginationSettings.variants?.disabled}
              onChange={checked => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  variants: {
                    disabled: checked
                  }
                });
              }}
            />
          </GridInput>
          <SwitchControlledCard
            header="Enable separators"
            labelProps={{
              label: 'Enable separators between notices',
              value: !!relevantPublisherPaginationSettings.separators?.enabled,
              onChange: checked => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  separators: {
                    ...DEFAULT_SEPARATOR_CONFIG,
                    ...relevantPublisherPaginationSettings.separators,
                    enabled: checked
                  }
                });
              }
            }}
          >
            <Autocomplete
              id="includeColumns"
              options={Array.from({ length: 10 }, (_, i) => ({
                label: `${i + 1} column ads`,
                value: (i + 1).toString()
              }))}
              multiselect
              value={
                relevantPublisherPaginationSettings.separators?.includeColumns?.map(
                  col => col.toString()
                ) || []
              }
              onChange={value => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  separators: {
                    ...DEFAULT_SEPARATOR_CONFIG,
                    ...relevantPublisherPaginationSettings.separators,
                    includeColumns: value.map(val => parseInt(val, 10))
                  }
                });
              }}
              labelText="Include columns"
              placeholder="Select columns to include separators"
            />
          </SwitchControlledCard>
          <SwitchControlledCard
            header="Enable borders between columns"
            labelProps={{
              label: 'Enable borders around notices',
              value: !!relevantPublisherPaginationSettings.bordering,
              onChange: checked => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  bordering: checked
                    ? {
                        thickness: 1,
                        excludedColumns: []
                      }
                    : null
                });
              }
            }}
          >
            <TextField
              id="borderingThickness"
              labelText="Border thickness"
              type="number"
              min={0}
              max={10}
              step={1}
              value={
                relevantPublisherPaginationSettings.bordering?.thickness?.toString() ||
                ''
              }
              onChange={value => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  bordering: {
                    excludedColumns:
                      relevantPublisherPaginationSettings.bordering
                        ?.excludedColumns || [],
                    ...relevantPublisherPaginationSettings.bordering,
                    thickness: parseInt(value, 10)
                  }
                });
              }}
            />
            <Autocomplete
              options={Array.from({ length: 10 }, (_, i) => ({
                label: `${i + 1} column ads`,
                value: (i + 1).toString()
              }))}
              multiselect
              value={
                relevantPublisherPaginationSettings.bordering?.excludedColumns?.map(
                  col => col.toString()
                ) || []
              }
              id="exclude-columns"
              labelText="Exclude columns"
              onChange={value => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  bordering: {
                    thickness:
                      relevantPublisherPaginationSettings.bordering
                        ?.thickness || 1,
                    ...relevantPublisherPaginationSettings.bordering,
                    excludedColumns: value.map(val => parseInt(val, 10))
                  }
                });
              }}
            />
          </SwitchControlledCard>
        </div>
        <div className="text-lg font-bold">Headers</div>
        <SwitchControlledCard
          header="Enable headers"
          labelProps={{
            label: 'Enable headers',
            value: !!relevantPublisherPaginationSettings.headerSettings,
            onChange: checked => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                headerSettings: checked
                  ? {
                      headers: [
                        {
                          applyToColumns: [1],
                          width: 1,
                          url: '',
                          id: guidGenerator().slice(0, 5)
                        }
                      ]
                    }
                  : null
              });
            }
          }}
        >
          <GridInput fullWidth>
            <EditableCardArray
              baseHeader="Header"
              columns={2}
              archivable={{
                buttonText: 'Remove',
                confirmation: {
                  header: 'Remove header',
                  body: 'Are you sure you want to remove this header?'
                },
                shouldAllow: () => true
              }}
              createable={{
                buttonText: 'Add header',
                initialValue: {
                  applyToColumns: [1],
                  width: 1,
                  url: '',
                  id: guidGenerator().slice(0, 5)
                }
              }}
              Component={({ value: header, onChange }) => (
                <>
                  <GridInput>
                    <Autocomplete
                      id="applyToColumns"
                      labelText="Layouts with columns"
                      multiselect
                      options={Array.from({ length: 10 }, (_, i) => ({
                        label: `${i + 1} column layouts`,
                        value: (i + 1).toString()
                      }))}
                      value={header.applyToColumns.map(String)}
                      onChange={value => {
                        onChange({
                          ...header,
                          applyToColumns: value.map(Number)
                        });
                      }}
                    />
                  </GridInput>
                  <TextField
                    labelText="Header Width (columns)"
                    id="headerWidth"
                    type="number"
                    min={0}
                    max={10}
                    value={header.width.toString()}
                    onChange={value =>
                      onChange({ ...header, width: parseInt(value, 10) })
                    }
                  />
                  <GridInput fullWidth>
                    <TextField
                      labelText="Header URL"
                      id="headerUrl"
                      value={header.url}
                      onChange={value => onChange({ ...header, url: value })}
                    />
                  </GridInput>
                </>
              )}
              onChange={headers => {
                setRelevantPublisherPaginationSettings({
                  ...relevantPublisherPaginationSettings,
                  headerSettings: {
                    ...relevantPublisherPaginationSettings.headerSettings,
                    headers
                  }
                });
              }}
              values={
                relevantPublisherPaginationSettings.headerSettings?.headers ||
                []
              }
            />
          </GridInput>
        </SwitchControlledCard>
        <div className="text-lg font-bold">Optimization Settings</div>
        <div className="grid grid-cols-2 gap-4">
          <TextField
            labelText="Max Time to run optimization (seconds)"
            id="max_time_seconds"
            type="number"
            min={10}
            max={10000}
            value={
              relevantPublisherPaginationSettings.optimizerHyperparams?.max_time_seconds?.toString() ||
              DEFAULT_OPTIMIZER_HYPERPARAMS.max_time_seconds.toString()
            }
            onChange={value => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                optimizerHyperparams: {
                  ...DEFAULT_OPTIMIZER_HYPERPARAMS,
                  ...relevantPublisherPaginationSettings.optimizerHyperparams,
                  max_time_seconds: parseInt(value, 10)
                }
              });
            }}
          />
          <TextField
            labelText="Max whitespace percentage (%)"
            id="max_whitespace_percentage"
            type="number"
            min={3}
            max={20}
            step={1}
            value={
              relevantPublisherPaginationSettings.optimizerHyperparams?.max_whitespace_percentage?.toString() ||
              DEFAULT_OPTIMIZER_HYPERPARAMS.max_whitespace_percentage.toString()
            }
            onChange={value => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                optimizerHyperparams: {
                  ...DEFAULT_OPTIMIZER_HYPERPARAMS,
                  ...relevantPublisherPaginationSettings.optimizerHyperparams,
                  max_whitespace_percentage: parseFloat(value)
                }
              });
            }}
          />
          <TextField
            labelText="Min Element Margin (points)"
            id="min_element_margin"
            type="number"
            min={0}
            max={100}
            value={
              relevantPublisherPaginationSettings.optimizerHyperparams?.min_element_margin?.toString() ||
              DEFAULT_OPTIMIZER_HYPERPARAMS.min_element_margin.toString()
            }
            onChange={value => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                optimizerHyperparams: {
                  ...DEFAULT_OPTIMIZER_HYPERPARAMS,
                  ...relevantPublisherPaginationSettings.optimizerHyperparams,
                  min_element_margin: parseInt(value, 10)
                }
              });
            }}
          />
          <TextField
            labelText="Max Filler Percentage (%)"
            id="max_filler_percentage"
            type="number"
            min={0}
            max={100}
            value={
              relevantPublisherPaginationSettings.optimizerHyperparams?.max_filler_percentage?.toString() ||
              DEFAULT_OPTIMIZER_HYPERPARAMS.max_filler_percentage.toString()
            }
            onChange={value => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                optimizerHyperparams: {
                  ...DEFAULT_OPTIMIZER_HYPERPARAMS,
                  ...relevantPublisherPaginationSettings.optimizerHyperparams,
                  max_filler_percentage: parseInt(value, 10)
                }
              });
            }}
          />
        </div>
        <div className="flex items-center gap-2 justify-between">
          <div className="text-lg font-bold">Layouts</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <TextField
            labelText="Min Layout Height (points)"
            id="minHeight"
            type="number"
            min={72}
            max={1000}
            step={36}
            value={
              relevantPublisherPaginationSettings.layoutSettings?.minHeight?.toString() ||
              ''
            }
            onChange={value => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                layoutSettings: {
                  ...DEFAULT_LAYOUT_SETTINGS,
                  ...relevantPublisherPaginationSettings.layoutSettings,
                  minHeight: parseInt(value, 10)
                }
              });
            }}
          />
          <TextField
            labelText="Layout Height Increments (points)"
            id="heightIncrements"
            type="number"
            min={0}
            max={1000}
            step={18}
            value={
              relevantPublisherPaginationSettings.layoutSettings?.heightIncrements?.toString() ||
              ''
            }
            onChange={value => {
              setRelevantPublisherPaginationSettings({
                ...relevantPublisherPaginationSettings,
                layoutSettings: {
                  ...DEFAULT_LAYOUT_SETTINGS,
                  ...relevantPublisherPaginationSettings.layoutSettings,
                  heightIncrements: parseInt(value, 10)
                }
              });
            }}
          />
        </div>
        <div className="text-lg font-bold">Fillers</div>
        <EditableCardArray
          baseHeader="Filler"
          columns={2}
          Component={({ value: filler, onChange }) => (
            <div key={filler.id} className="grid grid-cols-2 gap-4 col-span-2">
              <GridInput>
                <TextField
                  labelText="Name"
                  id="name"
                  value={filler.name}
                  onChange={value => onChange({ ...filler, name: value })}
                />
              </GridInput>
              <TextField
                labelText="Columns"
                id="columns"
                type="number"
                min={1}
                max={10}
                value={filler.columns.toString()}
                onChange={value =>
                  onChange({ ...filler, columns: parseInt(value, 10) })
                }
              />
              <div className="col-span-2 flex items-center gap-4">
                <div className="flex-1">
                  <TextField
                    labelText="PDF URL"
                    id="pdfUrl"
                    value={filler.pdfUrl}
                    onChange={value => onChange({ ...filler, pdfUrl: value })}
                    type="url"
                  />
                </div>
                <img
                  src={
                    filler.pdfUrl
                      ? cdnIfy(unCdnify(filler.pdfUrl), {
                          cloudinaryTransformations: 'f_jpg'
                        })
                      : ''
                  }
                  className="w-24 h-24 object-contain"
                />
              </div>
            </div>
          )}
          onChange={fillers => {
            setRelevantPublisherPaginationSettings({
              ...relevantPublisherPaginationSettings,
              fillers
            });
          }}
          values={relevantPublisherPaginationSettings.fillers}
          archivable={{
            buttonText: 'Remove',
            confirmation: {
              header: 'Remove filler',
              body: 'Are you sure you want to remove this filler?'
            }
          }}
          createable={{
            buttonText: 'Add filler',
            initialValue: {
              id: guidGenerator().slice(0, 5),
              columns: 1,
              maxOccurrences: 1,
              name: '',
              pdfUrl: ''
            }
          }}
        />

        <GridInput fullWidth>
          <ColumnButton
            buttonText="Update Settings"
            type="submit"
            primary
            fullWidth
            disabled={saving || unchanged}
            loading={saving}
          />
        </GridInput>
      </div>
    </Form>
  );
}
